import { getConfig } from '../../../../config/config';
import { getCurrentIdToken } from '../../../../utils/firebase';
import {
  ANALYTICS_ENDPOINT,
  POSTHOG_ONLY_EVENTS,
  DUAL_SEND_EVENTS,
  SENSITIVE_PROPERTY_FIELDS,
  PII_NETWORK_FIELDS,
  SENSITIVE_QUERY_PARAMS,
  REDACTED_VALUE,
  MAX_RETRIES,
  RETRY_DELAY_MS,
  QUEUE_KEY,
  MAX_QUEUE_SIZE,
} from './constants';
import { getEnrichmentData } from './enrichment';

const API_ENDPOINT = getConfig('REACT_APP_CLOUD_API_ENDPOINT');

const MAX_SERIALIZE_DEPTH = 10;

/**
 * Depth-bounded deep clone that keeps only JSON-serializable values.
 *
 * Drops DOM nodes, Window, functions and symbols, and breaks circular
 * references. This is the guard against an event whose properties
 * accidentally reference a live, React-managed DOM node (e.g. an <a> element
 * carrying React's `__reactFiber$…`/`stateNode` back-pointer). Such a value
 * makes JSON.stringify throw "Converting circular structure to JSON", which
 * — because PostHog serializes the event synchronously while persisting it
 * during capture() — propagates out of capture() and trips the app's
 * AppErrorBoundary ("Something went wrong"). Running every event through this
 * before it is returned to PostHog (and before it is sent to our backend)
 * keeps a stray node from ever reaching a serializer.
 */
export function toJsonSafe(value, seen = new WeakSet(), depth = 0) {
  if (value === null || value === undefined) return value;

  const type = typeof value;
  if (type === 'function' || type === 'symbol') return undefined;
  if (type !== 'object') return value;

  // DOM nodes / window / events carry circular internals — drop them.
  if (typeof Node !== 'undefined' && value instanceof Node) return undefined;
  if (typeof Window !== 'undefined' && value instanceof Window) return undefined;
  if (typeof value.nodeType === 'number' && typeof value.nodeName === 'string') return undefined;

  if (value instanceof Date) return value.toISOString();
  if (depth >= MAX_SERIALIZE_DEPTH) return undefined;
  if (seen.has(value)) return undefined;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => toJsonSafe(item, seen, depth + 1));
  }

  const out = {};
  Object.keys(value).forEach((key) => {
    // React attaches these expandos to DOM nodes; never serialize them.
    if (key.indexOf('__reactFiber') === 0 || key.indexOf('__reactProps') === 0) return;
    const cleaned = toJsonSafe(value[key], seen, depth + 1);
    if (cleaned !== undefined) out[key] = cleaned;
  });
  return out;
}

/**
 * Checks if event should go to PostHog only (not backend)
 */
export function isPostHogOnlyEvent(eventName) {
  return POSTHOG_ONLY_EVENTS.includes(eventName);
}

/**
 * Checks if event should go to BOTH backend and PostHog
 */
export function isDualSendEvent(eventName) {
  return DUAL_SEND_EVENTS.includes(eventName);
}

/**
 * Queue failed events to localStorage for retry on next session
 */
function queueForRetry(payload) {
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    queue.push({ payload, timestamp: Date.now() });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch (e) {
    // localStorage might be full or disabled
  }
}

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * Send event to backend with retry logic.
 * Uses fetch directly (not apiFetch) to handle 204 empty-body
 * responses without failing on res.json().
 */
async function sendToBackend(payload, retryCount = 0) {
  try {
    const token = await getCurrentIdToken();
    const res = await fetch(
      `${API_ENDPOINT}${ANALYTICS_ENDPOINT}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      await delay(RETRY_DELAY_MS * (2 ** retryCount));
      await sendToBackend(payload, retryCount + 1);
      return;
    }
    queueForRetry(payload);
  }
}

/**
 * Process queued events from previous sessions.
 * Clears only after all sends settle so a tab crash
 * mid-flight won't lose queued events.
 */
export async function processRetryQueue() {
  try {
    const queue = JSON.parse(
      localStorage.getItem(QUEUE_KEY) || '[]',
    );
    if (queue.length === 0) return;

    const processedCount = queue.length;

    await Promise.allSettled(
      queue.map(({ payload }) => sendToBackend(payload)),
    );

    // Preserve items queued during processing (re-queued failures
    // or new events); only remove the batch we just processed.
    const current = JSON.parse(
      localStorage.getItem(QUEUE_KEY) || '[]',
    );
    const remaining = current.slice(processedCount);
    if (remaining.length > 0) {
      localStorage.setItem(
        QUEUE_KEY,
        JSON.stringify(remaining),
      );
    } else {
      localStorage.removeItem(QUEUE_KEY);
    }
  } catch (e) {
    // Ignore queue processing errors
  }
}

/**
 * Strips sensitive fields from an object (shallow).
 */
export function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return properties;

  const sanitized = { ...properties };
  SENSITIVE_PROPERTY_FIELDS.forEach((field) => {
    if (sanitized[field] !== undefined) {
      sanitized[field] = REDACTED_VALUE;
    }
  });
  return sanitized;
}

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A sensitive param is masked wherever it appears as `<delimiter><key>=<value>`:
// in a full href, a bare path, a referrer, or an `$elements_chain` attribute.
// The raw pass runs first and takes the value up to the next raw `&`/`#`, so an
// encoded URL value (`continueUrl=https%3A%2F%2F…`) is masked whole. The
// encoded pass then catches a link percent-encoded inside some other param
// (`?redirect=%2Fregister%3FoobCode%3D…%26mode%3DsignIn`), where the value
// ends at the next encoded delimiter instead.
const SENSITIVE_QUERY_PARAM_KEYS = [...SENSITIVE_QUERY_PARAMS].map(escapeRegExp).join('|');
const QUERY_PARAM_VALUE_CHAR = '[^&#\\s"\'<>]';
const RAW_SENSITIVE_QUERY_PARAM_PATTERN = new RegExp(
  `([?&#;](?:${SENSITIVE_QUERY_PARAM_KEYS})=)${QUERY_PARAM_VALUE_CHAR}+`,
  'gi',
);
const ENCODED_SENSITIVE_QUERY_PARAM_PATTERN = new RegExp(
  `((?:%3F|%26|%23)(?:${SENSITIVE_QUERY_PARAM_KEYS})(?:=|%3D))(?:(?!%26|%23)${QUERY_PARAM_VALUE_CHAR})+`,
  'gi',
);

/**
 * Masks the value of every SENSITIVE_QUERY_PARAMS param in a string, keeping
 * the key and every other byte as it was.
 *
 * Nothing else scrubs URLs: the body sanitizers only see request/response
 * payloads, a recorder captures every navigation (including a component's
 * `replaceState`), and PostHog copies the URL into $current_url, $referrer,
 * $initial_* person props and recording meta events. Without this, a Firebase
 * email-link `oobCode` or a `?locate=<street address>` lands in analytics and
 * session replay verbatim.
 *
 * Works on text rather than a parsed URL so the shape that goes in is the shape
 * that comes back (LogRocket reports paths as well as hrefs), and so URLs
 * embedded in larger strings are caught too. Non-strings pass through.
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return url;
  return url
    .replace(RAW_SENSITIVE_QUERY_PARAM_PATTERN, `$1${REDACTED_VALUE}`)
    .replace(ENCODED_SENSITIVE_QUERY_PARAM_PATTERN, `$1${REDACTED_VALUE}`);
}

/**
 * Applies sanitizeUrl to every string in a JSON-safe value (objects, arrays,
 * primitives), returning a new value. Expects toJsonSafe output: no cycles.
 */
export function sanitizeUrlsDeep(value) {
  if (typeof value === 'string') return sanitizeUrl(value);
  if (Array.isArray(value)) return value.map(sanitizeUrlsDeep);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((key) => {
      out[key] = sanitizeUrlsDeep(value[key]);
    });
    return out;
  }
  return value;
}

/**
 * Recursively strips PII fields from nested objects/arrays.
 * Used for LogRocket network body sanitization where API responses
 * have nested structures like { data: { email, firstName, ... } }.
 */
export function sanitizeNetworkBody(obj) {
  // String leaves can carry a URL (e.g. `returnUrl: '/register?code=…&pin=…'` in the
  // email-link requests), so mask SENSITIVE_QUERY_PARAMS in them too.
  if (typeof obj === 'string') return sanitizeUrl(obj);
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeNetworkBody(item));
  }

  const sanitized = {};
  Object.keys(obj).forEach((key) => {
    if (PII_NETWORK_FIELDS.has(key) && obj[key] !== null && obj[key] !== undefined) {
      sanitized[key] = REDACTED_VALUE;
    } else {
      sanitized[key] = sanitizeNetworkBody(obj[key]);
    }
  });
  return sanitized;
}

// posthog-js gzips recording payloads (FullSnapshot data, Mutation adds/texts/...)
// into latin1 strings and tags those entries with `cv`. Regexes cannot mask
// anything inside them and a chance match would corrupt the chunk, so only the
// uncompressed entries (Meta href, $url_changed, ...) go through sanitizeUrlsDeep.
const isCompressedSnapshotEntry = (entry) => Boolean(entry && typeof entry === 'object' && entry.cv);

function sanitizeEventPropertiesUrls(properties) {
  if (!properties || !Array.isArray(properties.$snapshot_data)) {
    return sanitizeUrlsDeep(properties);
  }
  const { $snapshot_data: snapshotData, ...rest } = properties;
  return {
    ...sanitizeUrlsDeep(rest),
    $snapshot_data: snapshotData.map((entry) => (
      isCompressedSnapshotEntry(entry) ? entry : sanitizeUrlsDeep(entry)
    )),
  };
}

/**
 * Redacts PII_NETWORK_FIELDS in an application/x-www-form-urlencoded body, e.g.
 * Firebase's securetoken refresh request `grant_type=refresh_token&refresh_token=…`.
 * Returns the input unchanged when it is not a form-encoded string.
 */
export function sanitizeFormEncodedBody(body) {
  if (typeof body !== 'string' || !body.includes('=')) return body;
  try {
    const params = new URLSearchParams(body);
    let changed = false;
    params.forEach((value, key) => {
      if (PII_NETWORK_FIELDS.has(key)) {
        params.set(key, REDACTED_VALUE);
        changed = true;
      } else {
        const masked = sanitizeUrl(value);
        if (masked !== value) {
          params.set(key, masked);
          changed = true;
        }
      }
    });
    return changed ? params.toString() : body;
  } catch (e) {
    return body;
  }
}

/**
 * Sanitize a PostHog event before it reaches PostHog servers.
 * Strips sensitive values from properties and autocapture element data.
 */
function sanitizePostHogEvent(event) {
  if (!event) return event;

  const sanitized = {
    ...event,
    properties: sanitizeProperties(event.properties),
    ...(event.$set && { $set: sanitizeProperties(event.$set) }),
    ...(event.$set_once && { $set_once: sanitizeProperties(event.$set_once) }),
  };

  // Autocapture events may carry input values in $elements
  if (sanitized.properties?.$elements) {
    sanitized.properties.$elements = sanitized.properties.$elements.map((el) => {
      const cleanEl = { ...el };
      if (cleanEl.value !== undefined) {
        cleanEl.value = REDACTED_VALUE;
      }
      return cleanEl;
    });
  }

  // Final guard: strip any DOM nodes / circular references so PostHog can
  // always serialize this event. `toJsonSafe` returns undefined only for a
  // nullish top-level input, which cannot happen here (sanitized is an object).
  //
  // Then mask secret query params in every string: PostHog writes the page URL
  // into many properties ($current_url, $referrer, $initial_current_url,
  // $set_once person props, $snapshot meta/url_changed events, autocapture
  // hrefs), so masking by key would always be one property behind.
  sanitized.properties = sanitizeEventPropertiesUrls(toJsonSafe(sanitized.properties));
  if (sanitized.$set) sanitized.$set = sanitizeUrlsDeep(toJsonSafe(sanitized.$set));
  if (sanitized.$set_once) sanitized.$set_once = sanitizeUrlsDeep(toJsonSafe(sanitized.$set_once));

  return sanitized;
}

/**
 * Strips PostHog internal properties from event data before sending to backend.
 * PostHog adds $-prefixed metadata ($os, $browser, $device_type, $lib, etc.)
 * and meta fields (token, distinct_id) to every event. These are not needed
 * in the backend payload — useful metadata is already captured in context.
 */
const POSTHOG_META_FIELDS = new Set(['token', 'distinct_id']);

function stripPostHogProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return properties;

  const cleaned = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (!key.startsWith('$') && !POSTHOG_META_FIELDS.has(key)) {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

/**
 * Builds the payload for backend
 * API spec: eventType, source (required), userId, data, context (optional)
 */
function buildBackendPayload(event) {
  const enrichment = getEnrichmentData();

  return {
    eventType: event.event,
    source: enrichment.source,
    userId: event.properties?.distinct_id || event.distinct_id || null,
    data: {
      ...sanitizeUrlsDeep(toJsonSafe(sanitizeProperties(stripPostHogProperties(event.properties)))),
      platform: enrichment.platform,
    },
    context: {
      page: sanitizeUrl(window.location.href),
      referrer: sanitizeUrl(enrichment.referrer),
      userAgent: enrichment.userAgent,
      sessions: {
        posthog: {
          sessionId: event.properties?.$session_id || null,
          distinctId: event.properties?.distinct_id || event.distinct_id || null,
        },
      },
      utm: {
        utm_source: enrichment.utm_source || null,
        utm_medium: enrichment.utm_medium || null,
        utm_campaign: enrichment.utm_campaign || null,
        utm_term: enrichment.utm_term || null,
        utm_content: enrichment.utm_content || null,
        gclid: enrichment.gclid || null,
        fbclid: enrichment.fbclid || null,
        msclkid: enrichment.msclkid || null,
        ttclid: enrichment.ttclid || null,
        gbraid: enrichment.gbraid || null,
        wbraid: enrichment.wbraid || null,
        irclickid: enrichment.irclickid || null,
      },
    },
  };
}

/**
 * Creates the before_send hook for PostHog config.
 * - PostHog-only events ($pageview, $autocapture, etc.) → PostHog only
 * - Dual-send events → backend + PostHog
 * - Custom events → backend only (blocked from PostHog)
 */
export function createBeforeSendHook() {
  return (event) => {
    // before_send runs synchronously inside posthog.capture(). A throw here
    // (e.g. a circular/DOM value during serialization) would propagate out of
    // capture() and, when capture is called from a React render/effect, trip
    // the app's error boundary. Never let analytics take the app down.
    try {
      if (!event) return null;

      const eventName = event.event;

      // PostHog-only events: don't send to backend, let PostHog handle
      if (isPostHogOnlyEvent(eventName)) {
        return sanitizePostHogEvent(event);
      }

      // Dual-send events: send to backend AND PostHog
      if (isDualSendEvent(eventName)) {
        sendToBackend(buildBackendPayload(event));
        return sanitizePostHogEvent(event);
      }

      // Custom events: send to backend only, block from PostHog
      sendToBackend(buildBackendPayload(event));
      return null;
    } catch (e) {
      // Drop the event rather than crash the page. Returning null tells
      // PostHog the event was rejected by before_send.
      return null;
    }
  };
}

// Legacy class kept for backwards compatibility if needed
class AnalyticsMiddleware {
  constructor(posthog) {
    this.posthog = posthog;
    this.initialized = false;
  }

  init() {
    if (this.initialized || !this.posthog) return;
    processRetryQueue();
    this.initialized = true;
  }
}

export default AnalyticsMiddleware;
